/* eslint-disable @angular-eslint/prefer-inject */
import { Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    InventoryBundleAdjustmentViewModel,
    InventoryBundlePartialMoveViewModel,
    InventoryBundlesService,
    InventoryWithdrawViewModel,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { InventoryBundleStore } from './inventory-bundle.store';
import {
    InventoryBundleCreate,
    InventoryBundleRead,
    InventoryBundleSearchParams,
} from '../../entities/inventory-bundle.dto';
import { Operation } from 'fast-json-patch';

@Injectable()
export class InventoryBundleDataService extends AbstractDataService<
    InventoryBundleRead,
    InventoryBundleCreate
> {
    protected EntityName = PFWmsEntities.InventoryBundle;
    constructor(
        private service: InventoryBundlesService,
        store: InventoryBundleStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(
        body: InventoryBundleCreate
    ): Observable<InventoryBundleRead> {
        return this.service.inventoryBundleCreate({
            inventoryBundleViewModelCreateBody: body,
        }) as Observable<InventoryBundleRead>;
    }
    protected deleteObs$(id: string): Observable<InventoryBundleRead> {
        return this.service.inventoryBundleDelete({
            id,
        }) as Observable<InventoryBundleRead>;
    }
    protected undoDeleteObs$(id: string): Observable<InventoryBundleRead> {
        return this.service.inventoryBundleUndoDelete({
            id,
        }) as Observable<InventoryBundleRead>;
    }
    protected getObs$(id: string): Observable<InventoryBundleRead> {
        return this.service.inventoryBundleReadById({
            id,
        }) as Observable<InventoryBundleRead>;
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<InventoryBundleRead> {
        return this.service.inventoryBundleUpdatePatch({
            id,
            operation: operations,
        }) as Observable<InventoryBundleRead>;
    }
    protected searchObs$(
        searchParams: InventoryBundleSearchParams
    ): Observable<IPagedResult<InventoryBundleRead>> {
        return this.service.inventoryBundleSearch(searchParams) as Observable<
            IPagedResult<InventoryBundleRead>
        >;
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<InventoryBundleRead>>> {
        return this.service.inventoryBundleGetChanges(
            searchParams
        ) as Observable<
            IPagedResult<EntityChangeTrackingDto<InventoryBundleRead>>
        >;
    }

    partialMove$(
        bundleId: string,
        moveModel: InventoryBundlePartialMoveViewModel
    ) {
        return this.service
            .v1InventoryBundlesIdPartialMovePost({
                id: bundleId,
                inventoryBundlePartialMoveViewModel: moveModel,
            })
            .pipe(
                map(
                    response =>
                        response.destinationBundle as InventoryBundleRead
                ),
                tap(response => {
                    this.store.update(response);
                })
            );
    }

    withdrawInventory$(
        bundleId: string,
        withdrawModel: InventoryWithdrawViewModel
    ) {
        return this.service
            .v1InventoryBundlesIdWithdrawPost({
                id: bundleId,
                inventoryWithdrawViewModel: withdrawModel,
            })
            .pipe(
                map(response => response.affectedBundle as InventoryBundleRead),
                tap(response => {
                    this.store.update(response);
                })
            );
    }

    adjustInventory$(adjustmentModel: InventoryBundleAdjustmentViewModel) {
        return this.service
            .v1InventoryAdjustmentsPost({
                inventoryBundleAdjustmentViewModel: adjustmentModel,
            })
            .pipe(
                map(response => response.bundle as InventoryBundleRead),
                tap(response => {
                    this.store.update(response);
                })
            );
    }
}
